/**
 * Verifica della at_map e checklist di conformita'.
 *
 * La at_map non e' una sitemap: stabilisce una **corrispondenza fra URL e nodo
 * dell'alberatura**, e la sua divergenza dal sito e' verificabile
 * automaticamente da chiunque — ANAC lo fa con TrasparenzAI. Il pannello di
 * conformita' fa la stessa verifica dall'interno: confronta `GET /at_map.json`
 * con l'alberatura restituita da `GET /api/v1/nodi` e mostra le divergenze.
 *
 * Le voci verificabili dal client sono calcolate qui; quelle che richiedono un
 * accertamento umano o un run di `pa-website-validator` restano marcate come
 * manuali, senza essere spuntate d'ufficio. Fonte della checklist:
 * `.claude/skills/design-pa/references/checklist-pre-rilascio.md`.
 */

import type { AtMap, Nodo } from "../api/types";
import { CATALOGO_NODI } from "../data/catalogo-nodi";
import { identificativiAntenati, percorsoPubblicoNodo } from "./alberatura";
import { appiattisciAlberatura } from "./scadenze";

/**
 * Sezioni di primo livello previste, **contate dal catalogo** e non scritte a
 * mano.
 *
 * Erano 22 fino al 15/09/2026, quando «Bandi di concorso» e' salito al primo
 * livello: un numero cablato qui e' rimasto indietro rispetto al seed, e la
 * checklist avrebbe dichiarato «divergente» un'alberatura corretta. Il catalogo
 * e' generato dal seed, quindi il conteggio segue la fonte invece di inseguirla.
 */
const SEZIONI_A_CATALOGO = CATALOGO_NODI.filter((voce) => voce.livello === 1).length;

export type EsitoVerifica = "conforme" | "divergente" | "manuale" | "sconosciuto";

/** Una voce della checklist di conformita'. */
export interface VoceConformita {
  chiave: string;
  sezione: string;
  descrizione: string;
  esito: EsitoVerifica;
  /** Dettaglio dell'esito, in italiano. */
  dettaglio: string;
  /** Riferimento normativo o criterio 1.4.1. */
  riferimento: string;
}

/** Esito del confronto fra at_map e alberatura. */
export interface DiagnosiAtMap {
  raggiungibile: boolean;
  /** Messaggio d'errore se la at_map non e' stata letta. */
  errore?: string;
  voci_totali: number;
  /** Nodi presenti nell'alberatura ma assenti dalla at_map. */
  nodi_mancanti: Nodo[];
  /** Chiavi presenti nella at_map ma non corrispondenti ad alcun nodo. */
  chiavi_orfane: string[];
  /** Nodi la cui URL nella at_map differisce da quella dell'alberatura. */
  url_divergenti: { id_nodo: string; url_alberatura: string; url_at_map: string }[];
  /**
   * Nodi il cui URL non corrisponde alla loro **posizione** nell'alberatura.
   *
   * E' un controllo diverso da `url_divergenti`, che confronta la at_map con la
   * colonna `url`: due letture dello stesso valore memorizzato, che coincidono
   * sempre. Qui il termine di paragone e' il percorso ricavato dalla catena
   * degli antenati, l'unico modo per accorgersi di un obbligo di terzo livello
   * pubblicato a un indirizzo a due segmenti.
   */
  url_non_canonici: { id_nodo: string; url_pubblicato: string; url_atteso: string }[];
}

/** Confronta la at_map pubblicata con l'alberatura dell'ente. */
export function diagnosticaAtMap(
  atMap: AtMap | null,
  errore: string | null,
  nodi: readonly Nodo[],
): DiagnosiAtMap {
  if (!atMap) {
    return {
      raggiungibile: false,
      errore: errore ?? "at_map non disponibile.",
      voci_totali: 0,
      nodi_mancanti: [],
      chiavi_orfane: [],
      url_divergenti: [],
      url_non_canonici: [],
    };
  }

  const elencoNodi = appiattisciAlberatura(nodi);
  const perId = new Map(elencoNodi.map((nodo) => [nodo.id_nodo, nodo]));

  const nodiMancanti = elencoNodi.filter((nodo) => !(nodo.id_nodo in atMap));
  const chiaviOrfane = Object.keys(atMap).filter((chiave) => !perId.has(chiave));
  const urlDivergenti = Object.entries(atMap)
    .filter(([chiave, url]) => {
      const nodo = perId.get(chiave);
      return Boolean(nodo && nodo.url && nodo.url !== url);
    })
    .map(([chiave, url]) => ({
      id_nodo: chiave,
      url_alberatura: perId.get(chiave)?.url ?? "",
      url_at_map: url,
    }));

  // Percorso canonico: la catena degli antenati, in kebab-case, sotto la radice
  // della sezione. Confrontarlo con la coda dell'URL pubblicato non dipende dal
  // dominio, che in sviluppo differisce da quello di esercizio.
  //
  // La formula sta in `./alberatura` e non qui: e' la copia unica, dentro il
  // back-office, di `api/app/services/percorsi.py`. Riscriverla in questo punto
  // significherebbe confrontare gli URL pubblicati con un canonico calcolato da
  // una formula diversa da quella che li ha prodotti — cioe' diagnosticare la
  // divergenza con lo strumento che la genera.
  const percorsoCanonico = (nodo: Nodo): string =>
    percorsoPubblicoNodo(nodo.id_nodo, identificativiAntenati(perId, nodo.id_padre));

  const urlNonCanonici = Object.entries(atMap)
    .map(([chiave, url]) => {
      const nodo = perId.get(chiave);
      if (!nodo) return null;
      const atteso = percorsoCanonico(nodo);
      let percorso: string;
      try {
        percorso = new URL(url).pathname.replace(/\/+$/, "");
      } catch {
        return null; // URL malformato: lo segnala gia' la verifica strutturale.
      }
      return percorso === atteso
        ? null
        : { id_nodo: chiave, url_pubblicato: url, url_atteso: atteso };
    })
    .filter((voce): voce is NonNullable<typeof voce> => voce !== null);

  return {
    raggiungibile: true,
    voci_totali: Object.keys(atMap).length,
    nodi_mancanti: nodiMancanti,
    chiavi_orfane: chiaviOrfane,
    url_divergenti: urlDivergenti,
    url_non_canonici: urlNonCanonici,
  };
}

/** Costruisce la checklist, spuntando solo cio' che e' verificabile dal client. */
export function costruisciChecklist(
  diagnosi: DiagnosiAtMap,
  nodi: readonly Nodo[],
): VoceConformita[] {
  const primoLivello = nodi.filter((nodo) => nodo.livello === 1);
  const nonApplicabili = appiattisciAlberatura(nodi).filter((nodo) => !nodo.applicabile);
  const senzaUrl = appiattisciAlberatura(nodi).filter((nodo) => !nodo.url);

  const voci: VoceConformita[] = [
    {
      chiave: "alberatura_primo_livello",
      sezione: "Struttura e raggiungibilita'",
      descrizione: `Alberatura completa a ${SEZIONI_A_CATALOGO} nodi di primo livello`,
      esito: primoLivello.length === SEZIONI_A_CATALOGO ? "conforme" : "divergente",
      dettaglio:
        primoLivello.length === SEZIONI_A_CATALOGO
          ? `I ${SEZIONI_A_CATALOGO} nodi di primo livello dell'ambito c1 sono presenti.`
          : `Rilevati ${primoLivello.length} nodi di primo livello invece di ${SEZIONI_A_CATALOGO}.`,
      riferimento: "d.lgs. 33/2013, allegato; albero ANAC c1",
    },
    {
      chiave: "url_nodi",
      sezione: "Struttura e raggiungibilita'",
      descrizione: "Ogni nodo espone un URL pubblico stabile",
      esito: senzaUrl.length === 0 ? "conforme" : "divergente",
      dettaglio:
        senzaUrl.length === 0
          ? "Tutti i nodi hanno un URL calcolato."
          : `${senzaUrl.length} nodi sono privi di URL: non sarebbero raggiungibili dai crawler.`,
      riferimento: "Specifica ANAC, raggiungibilita'",
    },
    {
      chiave: "non_applicabili",
      sezione: "Struttura e raggiungibilita'",
      descrizione: "Nodi non applicabili valorizzati con la dicitura prevista",
      esito: "conforme",
      dettaglio:
        nonApplicabili.length === 0
          ? "Nessun nodo e' dichiarato non applicabile."
          : `${nonApplicabili.length} nodi non applicabili: il portale espone automaticamente la dicitura ANAC al posto del contenuto.`,
      riferimento: "Specifica ANAC, obblighi non applicabili",
    },
    {
      chiave: "at_map_pubblicata",
      sezione: "URL, versioning e at_map",
      descrizione: "File at_map pubblicato in radice di dominio",
      esito: diagnosi.raggiungibile ? "conforme" : "divergente",
      dettaglio: diagnosi.raggiungibile
        ? `at_map.json risponde e contiene ${diagnosi.voci_totali} voci.`
        : (diagnosi.errore ?? "at_map.json non risponde."),
      riferimento: "Specifica ANAC, mappa dei link",
    },
    {
      chiave: "at_map_copertura",
      sezione: "URL, versioning e at_map",
      descrizione: "at_map allineata all'alberatura (nessun nodo mancante)",
      esito: !diagnosi.raggiungibile
        ? "sconosciuto"
        : diagnosi.nodi_mancanti.length === 0
          ? "conforme"
          : "divergente",
      dettaglio: !diagnosi.raggiungibile
        ? "Verifica non eseguibile: at_map non raggiungibile."
        : diagnosi.nodi_mancanti.length === 0
          ? "Ogni nodo dell'alberatura ha una voce nella at_map."
          : `${diagnosi.nodi_mancanti.length} nodi non compaiono nella at_map.`,
      riferimento: "Specifica ANAC, mappa dei link",
    },
    {
      chiave: "at_map_orfane",
      sezione: "URL, versioning e at_map",
      descrizione: "Nessuna voce della at_map priva di nodo corrispondente",
      esito: !diagnosi.raggiungibile
        ? "sconosciuto"
        : diagnosi.chiavi_orfane.length === 0
          ? "conforme"
          : "divergente",
      dettaglio: !diagnosi.raggiungibile
        ? "Verifica non eseguibile."
        : diagnosi.chiavi_orfane.length === 0
          ? "Nessuna voce orfana."
          : `Voci senza nodo: ${diagnosi.chiavi_orfane.join(", ")}.`,
      riferimento: "Specifica ANAC, mappa dei link",
    },
    {
      chiave: "at_map_url",
      sezione: "URL, versioning e at_map",
      descrizione: "URL della at_map coincidenti con quelli dell'alberatura",
      esito: !diagnosi.raggiungibile
        ? "sconosciuto"
        : diagnosi.url_divergenti.length === 0
          ? "conforme"
          : "divergente",
      dettaglio: !diagnosi.raggiungibile
        ? "Verifica non eseguibile."
        : diagnosi.url_divergenti.length === 0
          ? "Nessuna divergenza fra at_map e alberatura."
          : `${diagnosi.url_divergenti.length} URL divergenti.`,
      riferimento: "Specifica ANAC, stabilita' degli URL",
    },
    {
      chiave: "at_map_posizione",
      sezione: "URL, versioning e at_map",
      descrizione: "URL coerenti con la posizione del nodo nell'alberatura",
      esito: !diagnosi.raggiungibile
        ? "sconosciuto"
        : diagnosi.url_non_canonici.length === 0
          ? "conforme"
          : "divergente",
      dettaglio: !diagnosi.raggiungibile
        ? "Verifica non eseguibile."
        : diagnosi.url_non_canonici.length === 0
          ? "Ogni indirizzo pubblicato rispecchia la catena degli antenati del nodo."
          : `${diagnosi.url_non_canonici.length} nodi sono pubblicati a un indirizzo che non corrisponde al loro posto nell'alberatura: il portale non li risolve e la at_map ne pubblica il collegamento morto.`,
      riferimento: "Specifica ANAC, corrispondenza URL/obbligo",
    },
    {
      chiave: "at_map_robots",
      sezione: "URL, versioning e at_map",
      descrizione: "Direttiva ATmap: presente nel robots.txt del portale",
      esito: "manuale",
      dettaglio: "Da verificare sul portale pubblico: e' configurazione di deploy, non dato d'API.",
      riferimento: "Specifica ANAC, robots.txt",
    },
    {
      chiave: "versioning",
      sezione: "URL, versioning e at_map",
      descrizione: "Pattern <Obbligo>-YYYYMMDD-YYYYMMDD.ext e alias -latest",
      esito: "conforme",
      dettaglio:
        "Garantito dalla pipeline: il nome file e' generato dal sistema e la pubblicazione crea una versione invece di sovrascrivere.",
      riferimento: "CONVENTIONS §4 e §7.1",
    },
    {
      chiave: "pdf_scansione",
      sezione: "Dati e schemi",
      descrizione: "Nessun PDF contenente pagine da scansione",
      esito: "conforme",
      dettaglio:
        "Vincolo applicato in fase di caricamento: un PDF da scansione e' rifiutato in modo bloccante e non diventa mai pubblico.",
      riferimento: "Specifica ANAC, formati dei dati",
    },
    {
      chiave: "cf_beneficiario",
      sezione: "Dati e schemi",
      descrizione: "Codice fiscale del beneficiario rimosso dallo schema art. 4",
      esito: "conforme",
      dettaglio:
        "Il campo non esiste nei tracciati: sostituito dalla tipologia di beneficiario a opzione vincolata (delibera 481/2025).",
      riferimento: "Delibera ANAC 481/2025",
    },
    {
      chiave: "formati_italiani",
      sezione: "Dati e schemi",
      descrizione: "Campi elementari nei formati italiani previsti",
      esito: "conforme",
      dettaglio:
        "Date gg/mm/aaaa, importi n.nnn,dd, percentuali nn,dd: serializzati in un unico punto lato API e mascherati in ingresso dai campi del back-office.",
      riferimento: "Delibera ANAC 495/2024",
    },
    {
      chiave: "validator",
      sezione: "Conformita' AgID e criteri 1.4.1",
      descrizione: "pa-website-validator eseguito senza fallimenti",
      esito: "manuale",
      dettaglio: "Esito da acquisire dal run di collaudo sul portale pubblico.",
      riferimento: "Criteri PNRR 1.4.1",
    },
    {
      chiave: "lighthouse",
      sezione: "Conformita' AgID e criteri 1.4.1",
      descrizione: "Lighthouse mobile ≥ 50 su tutte le pagine del portale",
      esito: "manuale",
      dettaglio: "Misurato in CI sul portale pubblico, non dal back-office.",
      riferimento: "C.SI.4.1",
    },
    {
      chiave: "at_footer",
      sezione: "Conformita' AgID e criteri 1.4.1",
      descrizione: "Link «Amministrazione trasparente» solo nel footer",
      esito: "manuale",
      dettaglio:
        "Verifica sul portale pubblico: la navigazione e' fissata nel layout e non e' editabile da qui.",
      riferimento: "C.SI.1.6",
    },
    {
      chiave: "accessibilita",
      sezione: "Accessibilita'",
      descrizione: "Test con screen reader e navigazione da tastiera",
      esito: "manuale",
      dettaglio: "Accertamento umano richiesto prima del rilascio.",
      riferimento: "WCAG 2.1 AA",
    },
  ];

  return voci;
}

/** Conteggio delle voci per esito, per il riepilogo in testa al pannello. */
export function riepilogoChecklist(voci: readonly VoceConformita[]): Record<EsitoVerifica, number> {
  const riepilogo: Record<EsitoVerifica, number> = {
    conforme: 0,
    divergente: 0,
    manuale: 0,
    sconosciuto: 0,
  };
  for (const voce of voci) riepilogo[voce.esito] += 1;
  return riepilogo;
}
